import type { Counter, FileTotals, OfflineErrorKind, SyncedBimIndex, SyncPhase, SyncProgress } from "@novorender/api";

import { assertNever } from "utils/assertNever";

import type { IncrementalFailure, OfflineEstimate, OfflineSceneRecord, OfflineSceneView } from "./offlineSlice";

/**
 * What the offline widget shows, as pure projections of a scene's record (`offlineSlice.ts`) and
 * the viewer's context: the current scene's card, the lists of the scenes on the device,
 * and the summary mobile Settings shows. The UI calls incremental caching the automatic cache.
 */

/** What the current scene's card can offer, besides what each screen carries. */
export type CurrentSceneScreen =
    /** No offline storage in this browser, or the project not yet resolved. */
    | { kind: "unavailable" }
    /** A viewer scene: offline is managed on its project scene. */
    | { kind: "viewerScene" }
    /** No full download, though the automatic cache may hold some of the scene. Action: download. */
    | {
          kind: "notDownloaded";
          /** The scene's size, estimated as the card shows. */
          estimate: DownloadEstimate;
          /** What the automatic cache holds of the scene, for a scene it caches. */
          cached: CachedPart | undefined;
          /** Why the scene's enrolment for the automatic cache failed, and what to do. */
          automaticCacheFailed: ErrorDisplay | undefined;
          /** This tab's download failed before it claimed anything: why, and what to do. */
          downloadFailed: ErrorDisplay | undefined;
      }
    /** Enrolment found no offline manifest: the scene can't be had offline. */
    | { kind: "unsupported" }
    /**
     * A sync, before its first tick past the scan without `progress`: an indeterminate bar. Action:
     * pause, unless another tab runs it.
     */
    | { kind: "downloading"; elsewhere: boolean; progress: ProgressDisplay | undefined }
    /** A sync paused, or interrupted (reload, crash, closed tab). Action: resume. */
    | {
          kind: "resumable";
          reason: "paused" | "interrupted";
          /**
           * On disk, of {@link total} when known: both the stopped sync's own, toward its target, as
           * its state records them. Online too: a resume's
           * scan counts toward the version online then, and its progress says so.
           */
          downloaded: FileTotals;
          total: FileTotals | undefined;
          /**
           * Online, the version loaded isn't the stopped sync's target: a newer one is published, which
           * a resume downloads instead, so its numbers will differ from these.
           */
          newerVersion: boolean;
          /** This tab's resume failed before it claimed anything: why, and what to do. */
          downloadFailed: ErrorDisplay | undefined;
          /** This tab's discard failed: why, and what to do. */
          deleteFailed: ErrorDisplay | undefined;
      }
    /**
     * Complete and openable offline. Action: update, always: a sync also refreshes bookmarks,
     * groups and the mini map, which no version tells of.
     */
    | {
          kind: "synced";
          size: number;
          /** Epoch ms, when known. */
          syncedAt: number | undefined;
          /** Online, the loaded version isn't the synced one. */
          updateAvailable: boolean;
          /**
           * Online, the loaded version is the synced one, and nothing else is known to need an
           * update. Offline, and before the engine has loaded a version, that can't be known.
           */
          upToDate: boolean;
          /** The capture isn't of the files' version, or there is none: the scene won't open offline. */
          hostDataNeedsResync: boolean;
          /** The mini map's last sync left tiles out. */
          minimapIncomplete: boolean;
          /** BIM index entries the completed sync left out, as the SDK records it. */
          bimIndexSkipped: number;
          /** This tab's update failed before it changed anything: why, and what to do. */
          updateFailed: ErrorDisplay | undefined;
          /** This tab's delete failed: why, and what to do. */
          deleteFailed: ErrorDisplay | undefined;
      }
    /** This tab's sync failed. Actions: as the error's; the download resumes from `downloaded`. */
    | { kind: "error"; error: ErrorDisplay; downloaded: FileTotals | undefined };

/** The open scene's size, as estimated for its full download. */
export type DownloadEstimate =
    /** Asked for, or about to be: online, with none held for the scene. */
    | { kind: "pending" }
    /**
     * The whole scene. A download fetches less where the automatic cache already holds some of it:
     * the scene's size reads more plainly beside the cache's own count than what's left would.
     */
    | { kind: "ready"; bytes: number }
    /** The estimate failed, or can't be had offline. A download can still be asked for. */
    | { kind: "unknown" };

/** What the automatic cache holds of a scene, by its own count. */
export type CachedPart = { bytes: number };

export type CurrentSceneContext = {
    /** The open scene's record id, and its estimate's. */
    sceneDirName: string;
    /** The browser has offline storage (`OfflineViewState`). */
    hasOfflineStorage: boolean;
    /** `undefined` until init has resolved the project. */
    isProjectScene: boolean | undefined;
    isOnline: boolean;
    /**
     * The version the engine loaded, from the scene index it just fetched: its offline manifest's
     * name. Compared with the synced one, no request made.
     */
    loadedManifestId: string | undefined;
    estimate: OfflineEstimate | undefined;
    /**
     * Whether a full sync captures host data, as every backend but Procore's does. If so, the
     * scene isn't up to date until its capture has been read.
     */
    capturesHostData: boolean;
    incrementalFailure: IncrementalFailure | undefined;
};

export function currentSceneScreen(
    record: OfflineSceneRecord | undefined,
    context: CurrentSceneContext,
): CurrentSceneScreen {
    if (!context.hasOfflineStorage || context.isProjectScene === undefined) {
        return { kind: "unavailable" };
    }
    if (!context.isProjectScene) {
        return { kind: "viewerScene" };
    }

    const runFailed = record?.runFailure && errorDisplay(record.runFailure.kind);
    const notDownloaded = (cached: CachedPart | undefined): CurrentSceneScreen => ({
        kind: "notDownloaded",
        estimate: downloadEstimate(context),
        cached,
        automaticCacheFailed: automaticCacheFailed(context),
        downloadFailed: runFailed,
    });

    if (!record) {
        return notDownloaded(undefined);
    }

    const { view } = record;
    switch (view.kind) {
        case "absent":
            return notDownloaded(undefined);
        case "unsupported":
            return { kind: "unsupported" };
        case "incremental":
            return notDownloaded({ bytes: view.used.bytes });
        case "syncing":
            return {
                kind: "downloading",
                elsewhere: record.syncingElsewhere === true,
                progress: view.progress && progressDisplay(view.progress),
            };
        case "paused":
        case "interrupted":
            return {
                kind: "resumable",
                reason: view.kind,
                downloaded: view.downloaded,
                total: view.total,
                newerVersion: isNewerVersionLoaded(context, view.targetManifestId),
                downloadFailed: runFailed,
                deleteFailed: record.deleteFailure && deleteErrorDisplay(record.deleteFailure.kind),
            };
        case "synced": {
            const needsResync = hostDataNeedsResync(record);
            const incomplete = minimapIncomplete(view.minimap.state);
            return {
                kind: "synced",
                size: syncedSize(view),
                syncedAt: view.syncedAt ?? legacyDate(record.legacyLastSynced),
                updateAvailable: isNewerVersionLoaded(context, view.manifestId),
                upToDate:
                    isSameVersionLoaded(context, view.manifestId) &&
                    !(context.capturesHostData && record.capturedManifestId === undefined) &&
                    !needsResync &&
                    !incomplete &&
                    !record.runFailure,
                hostDataNeedsResync: needsResync,
                minimapIncomplete: incomplete,
                bimIndexSkipped: bimIndexSkipped(view.bimIndex),
                updateFailed: runFailed,
                deleteFailed: record.deleteFailure && deleteErrorDisplay(record.deleteFailure.kind),
            };
        }
        case "failed":
            return { kind: "error", error: errorDisplay(view.error.kind), downloaded: view.downloaded };
        default:
            return assertNever(view);
    }
}

/**
 * Whether a synced scene's capture is missing or of another version than its files, so the scene
 * won't open offline. False until the capture has been read, which happens only for the open scene.
 */
function hostDataNeedsResync({ view, capturedManifestId }: OfflineSceneRecord): boolean {
    return (
        view.kind === "synced" &&
        view.manifestId !== undefined &&
        capturedManifestId !== undefined &&
        capturedManifestId !== view.manifestId
    );
}

function automaticCacheFailed({ incrementalFailure, sceneDirName }: CurrentSceneContext): ErrorDisplay | undefined {
    return incrementalFailure?.sceneId === sceneDirName ? errorDisplay(incrementalFailure.error.kind) : undefined;
}

/** The open scene's estimate, as held for it. */
function downloadEstimate({ estimate, sceneDirName, isOnline }: CurrentSceneContext): DownloadEstimate {
    if (estimate?.sceneId !== sceneDirName) {
        return isOnline ? { kind: "pending" } : { kind: "unknown" };
    }
    switch (estimate.kind) {
        case "ready":
            return { kind: "ready", bytes: estimate.total.bytes };
        case "failed":
            return { kind: "unknown" };
        default:
            return assertNever(estimate);
    }
}

/**
 * Whether the version the engine loaded online isn't the one the scene's files are of, or were
 * downloading toward: a newer one is published. Unknowable offline, and before
 * the engine has loaded a version. No request: the loaded scene index names it.
 */
function isNewerVersionLoaded(context: CurrentSceneContext, manifestId: string | undefined): boolean {
    return isVersionKnown(context, manifestId) && context.loadedManifestId !== manifestId;
}

function isSameVersionLoaded(context: CurrentSceneContext, manifestId: string | undefined): boolean {
    return isVersionKnown(context, manifestId) && context.loadedManifestId === manifestId;
}

function isVersionKnown(context: CurrentSceneContext, manifestId: string | undefined): boolean {
    return context.isOnline && context.loadedManifestId !== undefined && manifestId !== undefined;
}

/** Which count a sync's progress is shown by. */
export type ProgressLead =
    /** Bytes: the download is bound by them. */
    | "bytes"
    /** Files: the brep phase is many small files, bound by latency, while bytes barely move. */
    | "files"
    /** The mini map callback's own count: the mini map isn't part of the scene's bytes or files. */
    | "minimap"
    /** No count: the phase's work isn't counted anywhere (host data, finalize). Its name alone. */
    | "none";

export type ProgressDisplay = {
    phase: Exclude<SyncPhase, "scan">;
    /** Which count the text leads with: the one that moves in this phase. */
    lead: ProgressLead;
    /** The lead count. */
    counter: Counter;
    /**
     * The bar, 0 to 1, by the bytes even in brep: a bar that followed the files would jump back
     * where brep begins and ends, as geometry is most of the bytes and few of the files.
     * Undefined, an indeterminate bar, while the bytes' total is unknown and in the phases after
     * the bytes (mini map, host data, finalize), so that a full bar means done.
     */
    fraction: number | undefined;
    bytes: Counter;
    files: Counter;
};

/** How a tick shows: `undefined` while the scan establishes the totals. */
export function progressDisplay(progress: SyncProgress): ProgressDisplay | undefined {
    const display = (
        phase: Exclude<SyncPhase, "scan">,
        lead: ProgressLead,
        counter: Counter,
        bar: Counter | "indeterminate" = progress.bytes,
    ): ProgressDisplay => ({
        phase,
        lead,
        counter,
        fraction: bar === "indeterminate" ? undefined : fraction(bar),
        bytes: progress.bytes,
        files: progress.files,
    });

    const { phase } = progress;
    switch (phase) {
        case "scan":
            return undefined;
        case "brep":
            return display(phase, "files", progress.files);
        case "minimap":
            // The callback's own count, in the text; the bar spins (see `fraction`).
            return display(phase, "minimap", progress.minimap ?? { done: 0, total: undefined }, "indeterminate");
        case "hostData":
        case "finalize":
            return display(phase, "none", progress.bytes, "indeterminate");
        case "geometry":
        case "db":
        case "bimIndex":
            return display(phase, "bytes", progress.bytes);
        default:
            return assertNever(phase);
    }
}

/**
 * How far this tab's full sync is by bytes: the scene's and the mini map's estimate
 * (`OfflineSceneRecord.minimapBytes`), shared out by the mini map phase's file count.
 * @returns 0 to 1; `undefined`, an indeterminate bar, while the scan counts the scene's bytes, in
 *   the mini map phase without an estimate, and in the phases after it, so that a full bar means done.
 */
export function syncByteFraction(record: OfflineSceneRecord | undefined): number | undefined {
    if (record?.view.kind !== "syncing" || !record.view.progress) {
        return undefined;
    }
    const { progress } = record.view;
    const { phase } = progress;
    switch (phase) {
        case "scan":
        case "hostData":
        case "finalize":
            return undefined;
        case "minimap":
            if (record.minimapBytes === undefined) {
                return undefined;
            }
            break;
        case "geometry":
        case "brep":
        case "db":
        case "bimIndex":
            break;
        default:
            return assertNever(phase);
    }
    if (progress.bytes.total === undefined) {
        return undefined;
    }
    const minimapTotal = record.minimapBytes ?? 0;
    const minimapDone = (fraction(progress.minimap ?? { done: 0, total: undefined }) ?? 0) * minimapTotal;
    return fraction({
        done: progress.bytes.done + minimapDone,
        total: progress.bytes.total + minimapTotal,
    });
}

/** What an error offers: retry, or guidance on freeing space before a retry. */
export type ErrorAction = "retry" | "freeSpace";

export type ErrorDisplay = {
    kind: OfflineErrorKind;
    /** Translation key of what went wrong. */
    message: string;
    action: ErrorAction;
};

/** The copy and action for each kind of failure. */
export function errorDisplay(kind: OfflineErrorKind): ErrorDisplay {
    switch (kind) {
        case "quotaExceeded":
            return { kind, message: "notEnoughSpaceError", action: "freeSpace" };
        case "offline":
            return { kind, message: "networkDownloadError", action: "retry" };
        case "downloadFailed":
            return { kind, message: "offlineWidget.error.downloadFailed", action: "retry" };
        case "invalidManifest":
            return { kind, message: "offlineWidget.error.invalidManifest", action: "retry" };
        case "io":
            return { kind, message: "offlineWidget.error.io", action: "retry" };
        case "busy":
            return { kind, message: "offlineWidget.error.busy", action: "retry" };
        case "captureFailed":
            return { kind, message: "offlineWidget.error.captureFailed", action: "retry" };
        case "unknown":
            return { kind, message: "genericDownloadError", action: "retry" };
        default:
            return assertNever(kind);
    }
}

/**
 * The copy and action for a failed delete: as {@link errorDisplay}'s, but for `busy`. A delete is
 * refused by another tab's sync, and by a reader of the scene's files, such as search, that
 * didn't let go; a sync only by another tab.
 */
function deleteErrorDisplay(kind: OfflineErrorKind): ErrorDisplay {
    return kind === "busy" ? { kind, message: "offlineWidget.error.deleteBusy", action: "retry" } : errorDisplay(kind);
}

/** What novoweb calls a scene's offline status, for mobile Settings and the downloaded-scenes list. */
export type OfflineSceneStatus =
    | "incremental"
    | "scanning"
    | "synchronizing"
    | "synchronized"
    | "paused"
    | "interrupted"
    | "error"
    | "invalid format";

export type OfflineSceneSummary = {
    /** What the scene is called, as recorded; `undefined` where it was never given one, or an empty one. */
    name: string | undefined;
    status: OfflineSceneStatus;
    /**
     * Percentage of the current download's bytes, to two decimals ({@link ProgressDisplay}'s
     * fraction). Empty when unknown and in the phases after the bytes.
     */
    progress: string;
    /** Bytes on the device. */
    size: number;
    /** ISO date of the last completed sync, empty when unknown. */
    lastSync: string;
    errorKind?: OfflineErrorKind;
    syncingElsewhere: boolean;
};

/**
 * A scene's record, summarised. `undefined` for one with nothing offline, unless this tab's
 * download of it failed. A run that failed before its claim reads as `error`, as Mobile Settings
 * shows a failed download by it. A synced scene whose update failed stays `synchronized`, as its
 * copy is whole.
 */
export function offlineSceneSummary(record: OfflineSceneRecord | undefined): OfflineSceneSummary | undefined {
    if (!record) {
        return undefined;
    }
    const { view, runFailure } = record;
    if (runFailure && view.kind !== "synced") {
        return sceneSummary(record, "error", viewSummary(record)?.size ?? 0, { errorKind: runFailure.kind });
    }
    return viewSummary(record);
}

function sceneSummary(
    record: OfflineSceneRecord,
    status: OfflineSceneStatus,
    size: number,
    rest: Partial<OfflineSceneSummary> = {},
): OfflineSceneSummary {
    return {
        // The SDK keeps a name as it was given, an empty one too: nothing to show the scene by.
        name: record.name || undefined,
        status,
        progress: "",
        size,
        lastSync: "",
        syncingElsewhere: record.syncingElsewhere === true,
        ...rest,
    };
}

/** The summary of what the scene holds, whatever this tab's last run did. */
function viewSummary(record: OfflineSceneRecord): OfflineSceneSummary | undefined {
    const { view, legacyLastSynced } = record;
    const summary = (status: OfflineSceneStatus, size: number, rest?: Partial<OfflineSceneSummary>) =>
        sceneSummary(record, status, size, rest);
    switch (view.kind) {
        case "absent":
            return undefined;
        case "unsupported":
            return summary("invalid format", 0);
        case "incremental":
            return summary("incremental", view.used.bytes);
        case "syncing": {
            const progress = view.progress && progressDisplay(view.progress);
            return progress
                ? summary("synchronizing", progress.bytes.done, { progress: percent(progress.fraction) })
                : summary("scanning", view.progress?.bytes.done ?? 0);
        }
        case "paused":
        case "interrupted":
            return summary(view.kind, view.downloaded.bytes);
        case "synced": {
            const syncedAt = view.syncedAt ?? legacyDate(legacyLastSynced);
            return summary("synchronized", syncedSize(view), {
                lastSync: syncedAt !== undefined ? new Date(syncedAt).toISOString() : "",
            });
        }
        case "failed":
            return summary("error", view.downloaded?.bytes ?? 0, { errorKind: view.error.kind });
        default:
            return assertNever(view);
    }
}

/** A scene in one of the widget's lists. */
export type OfflineSceneRow = OfflineSceneSummary & {
    id: string;
    isCurrent: boolean;
    /** For a download that stopped short: what it has on disk, of its target when known. */
    partial: { downloaded: FileTotals; total: FileTotals | undefined } | undefined;
    /** This tab's download failed before it claimed anything, as the card shows it. */
    downloadFailed: ErrorDisplay | undefined;
    /** This tab's sync failed after it claimed the scene: why, and what to do. */
    syncFailed: ErrorDisplay | undefined;
    /** This tab's delete of the scene failed: why, and what to do. */
    deleteFailed: ErrorDisplay | undefined;
    /** As on the current scene's synced screen. */
    hostDataNeedsResync: boolean;
};

/**
 * The scenes on the device, in the widget's two lists: full downloads, a stopped or failed one too,
 * and the scenes the automatic cache holds. Each the current scene first, then by name. Listed by
 * what the scene holds: one with nothing offline isn't listed, even if this tab's download of it
 * failed, and nor is one that can't be had offline. A row's status is what the scene holds, as on
 * its card, and a failed download shows beside it.
 */
export function offlineSceneLists(
    records: readonly OfflineSceneRecord[],
    currentId: string,
): { downloaded: OfflineSceneRow[]; cached: OfflineSceneRow[] } {
    const lists = { downloaded: [] as OfflineSceneRow[], cached: [] as OfflineSceneRow[] };
    for (const record of records) {
        const { view, runFailure } = record;
        const summary = viewSummary(record);
        if (!summary || view.kind === "unsupported") {
            continue;
        }
        const showsDownloadFailed =
            view.kind === "incremental" || view.kind === "paused" || view.kind === "interrupted";
        const row: OfflineSceneRow = {
            ...summary,
            id: record.id,
            isCurrent: record.id === currentId,
            partial:
                view.kind === "paused" || view.kind === "interrupted"
                    ? { downloaded: view.downloaded, total: view.total }
                    : view.kind === "failed" && view.downloaded
                      ? { downloaded: view.downloaded, total: undefined }
                      : undefined,
            downloadFailed: runFailure && showsDownloadFailed ? errorDisplay(runFailure.kind) : undefined,
            syncFailed: view.kind === "failed" ? errorDisplay(view.error.kind) : undefined,
            deleteFailed: record.deleteFailure && deleteErrorDisplay(record.deleteFailure.kind),
            hostDataNeedsResync: hostDataNeedsResync(record),
        };
        (view.kind === "incremental" ? lists.cached : lists.downloaded).push(row);
    }
    const order = (a: OfflineSceneRow, b: OfflineSceneRow) =>
        Number(b.isCurrent) - Number(a.isCurrent) || (a.name ?? a.id).localeCompare(b.name ?? b.id);
    lists.downloaded.sort(order);
    lists.cached.sort(order);
    return lists;
}

/** The scene's files and its mini map's tiles: what it takes on the device. */
function syncedSize(view: Extract<OfflineSceneView, { kind: "synced" }>): number {
    return view.total.bytes + (view.minimap.total?.bytes ?? 0);
}

function minimapIncomplete(state: Extract<OfflineSceneView, { kind: "synced" }>["minimap"]["state"]): boolean {
    switch (state) {
        case "failed":
            return true;
        case "synced":
        case "unknown":
            return false;
        default:
            return assertNever(state);
    }
}

function bimIndexSkipped(bimIndex: SyncedBimIndex): number {
    switch (bimIndex.state) {
        case "stored":
            return bimIndex.skipped;
        case "absent":
        case "unknown":
            return 0;
        default:
            return assertNever(bimIndex);
    }
}

function legacyDate(iso: string | undefined): number | undefined {
    const time = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(time) ? undefined : time;
}

function fraction(counter: Counter): number | undefined {
    if (counter.total === undefined) {
        return undefined;
    }
    return counter.total === 0 ? 1 : Math.min(1, counter.done / counter.total);
}

function percent(fraction: number | undefined): string {
    return fraction === undefined ? "" : (fraction * 100).toFixed(2);
}
